import { endOfDay, startOfDay } from "date-fns";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import type { SlackFilters } from "@/app/components/SlackNewsFilters";
import { isTruthy } from "@/app/utils";
import { type NodeDef, wireNodes } from "@/app/utils/nodeDag";
import type { MemberExternalAccount } from "@/src/core/externalAccounts";
import type { Person } from "@/src/core/people";
import type { Project } from "@/src/core/projects";
import type { RecentOwnedItem } from "@/src/core/recentOwnedItems";
import type { Scope } from "@/src/core/scopes";
import type { Task } from "@/src/core/tasks";
import type { TeamTaskView } from "@/src/core/teamTaskViews";

export type SelectableScopeStatus = "Current" | "Future";
export type RoleFilter = "All" | "Core Team" | "Core Team - FT" | "Contractors" | "Other";
/**
 * A team member is the domain root for this app.
 *
 * Since identity and the concept of people/users are split
 * between notion users and rows in the team task views table,
 * this requires some friction on what it means to be a foreign key
 *
 * 1. Start with the team task view, that narrows us down to people who are assigned
 *    work and aren't bots. Also some people have multiple notion users
 * 2. Omit the person key, we'll map notionUserIds and notionUsers (which are still person type)
 * 3. Transform teams array into a string, don't support grouping yet
 */
export type TeamMember = Omit<TeamTaskView, "person" | "team"> & {
  notionUsers: Person[];
  externalAccounts: MemberExternalAccount[];
  // We use this for too many lookups to leave nested
  notionUserIds: string[];
  /** Test 1 2 3 */
  team: string;
};

type EntityType = "projects" | "scopes" | "tasks";

/**
 * memberEntitiesMap maps from teamMemberId to
 */
export interface InScope {
  projects: Set<string>;
  scopes: Set<string>;
  tasks: Set<string>;
  // map teamMemberIds to an array of tuples representing entity type and id
  // so that we can later iterate over 1 array to get all the relevant bits
  memberEntitiesMap: Map<string, [EntityType, string][]>;
}

export interface DagStore {
  // Fresh off the wire
  people: Person[];
  projects: Project[];
  tasks: Task[];
  scopes: Scope[];
  teamTaskViews: TeamTaskView[];
  recentOwnedItems: RecentOwnedItem[];
  memberExternalAccounts: MemberExternalAccount[];

  // derived collections ----------------------------------------

  teamMembers: TeamMember[]; // Unfiltered - all team members including archived
  teamMembersFiltered: TeamMember[]; // Filtered - excludes archived members (for UI)
  allTeams: string[];
  allProjects: Project[];

  // Indexes ----------------------------------------
  projectIndex: Map<string, Project>;
  taskIndex: Map<string, Task>;
  scopeIndex: Map<string, Scope>;
  teamMemberIndex: Map<string, TeamMember>;
  notionUIDToTeamMemberId: Map<string, string>;

  // Filters ----------------------------------------
  excludedTeams: Set<string>;
  excludedProjects: Set<string>;
  selectedScopeStatus: SelectableScopeStatus;
  selectedRoleFilter: RoleFilter;
  teamOrder: string[];
  slackFilters: SlackFilters;
  cardsCollapsed: boolean;

  // Derivations of filters --------------------------------------
  inScope: InScope; // what's in scope? very popular question

  // Setters ----------------------------------------
  setRaw(
    this: void,
    patch: Partial<
      Pick<
        DagStore,
        | "people"
        | "projects"
        | "tasks"
        | "scopes"
        | "teamTaskViews"
        | "recentOwnedItems"
        | "memberExternalAccounts"
      >
    >,
  ): void;
  setFilters(
    this: void,
    patch: Partial<
      Pick<
        DagStore,
        "selectedScopeStatus" | "excludedTeams" | "excludedProjects" | "selectedRoleFilter"
      >
    >,
  ): void;

  // Convenience Actions ----------------------------------------
  toggleTeam(this: void, teamId: string): void;
  toggleProject(this: void, projectId: string): void;
  toggleScopeStatus(this: void): void;
  setRoleFilter(this: void, filter: RoleFilter): void;
  setExcludedTeams(this: void, teams: string[]): void;
  setExcludedProjects(this: void, projects: string[]): void;
  resetFilters(this: void): void;
  setTeamOrder(this: void, order: string[]): void;
  initializeRandomTeamOrder(this: void): void;
  setSlackFilters(this: void, filters: SlackFilters): void;
  toggleCardsCollapsed(this: void): void;
}

// Helpers ----------------------------------------
const createIndex = <T extends { id: string }>(items: T[]): Record<string, T> => {
  return items.reduce(
    (acc, item) => {
      acc[item.id] = item;
      return acc;
    },
    {} as Record<string, T>,
  );
};

const buildTeamMembers = (
  people: Person[],
  teamTaskViews: TeamTaskView[],
  memberExternalAccounts: MemberExternalAccount[],
): TeamMember[] => {
  const peopleById = createIndex(people);
  const externalAccountsByMemberId = new Map<string, MemberExternalAccount[]>();
  for (const account of memberExternalAccounts) {
    const existing = externalAccountsByMemberId.get(account.memberId) ?? [];
    existing.push(account);
    externalAccountsByMemberId.set(account.memberId, existing);
  }

  return teamTaskViews.map((view) => {
    const notionUsers = view.person.map((id) => peopleById[id]).filter(isTruthy);
    const notionUserIds = notionUsers.map((user) => user.id);
    return {
      ...view,
      externalAccounts: externalAccountsByMemberId.get(view.id) ?? [],
      notionUserIds,
      notionUsers,
      team: view.team.join(" > "),
    };
  });
};

const buildTeamMembersFiltered = (teamMembers: TeamMember[]): TeamMember[] => {
  return teamMembers.filter((member) => {
    // Filter out archived members - check multiple conditions to be thorough
    const isArchived = member.status.some(
      (s) => s.toLowerCase() === "archived" || s.toLowerCase().includes("archive"),
    );
    return !isArchived;
  });
};

const buildTeamMemberIndex = (teamMembers: TeamMember[]): Map<string, TeamMember> => {
  return new Map(teamMembers.map((member) => [member.id, member]));
};

const buildAllTeams = (teamTaskViews: TeamTaskView[], customOrder?: string[]): string[] => {
  const teamSet = new Set<string>();
  teamTaskViews.forEach((view) => {
    const teamString = view.team.join(" > ");
    // Only add non-empty team strings to avoid blank entries in the filter
    if (teamString.trim() !== "") {
      teamSet.add(teamString);
    }
  });
  const allTeams = Array.from(teamSet);

  if (customOrder && customOrder.length > 0) {
    // Sort by custom order, fallback to alphabetical for new teams
    return [
      ...customOrder.filter((team) => allTeams.includes(team)),
      ...allTeams.filter((team) => !customOrder.includes(team)).sort(),
    ];
  }

  return allTeams.sort();
};

const buildAllProjects = (projects: Project[]): Project[] => {
  // Return sorted projects by name for display
  return [...projects].sort((a, b) => a.projectName.localeCompare(b.projectName));
};

const buildProjectIndex = (projects: Project[]): Map<string, Project> => {
  return new Map(projects.map((project) => [project.id, project]));
};

const buildTaskIndex = (tasks: Task[]): Map<string, Task> => {
  return new Map(tasks.map((task) => [task.id, task]));
};

const buildScopeIndex = (scopes: Scope[]): Map<string, Scope> => {
  return new Map(scopes.map((scope) => [scope.id, scope]));
};

const buildNotionUIDToTeamMemberId = (
  teamMembers: TeamMember[],
  people: Person[],
): Map<string, string> => {
  const output = new Map<string, string>();

  // Build email to person IDs mapping to find duplicate accounts
  const emailToPeopleIds = new Map<string, string[]>();
  for (const person of people) {
    if (person.email) {
      const existing = emailToPeopleIds.get(person.email) || [];
      existing.push(person.id);
      emailToPeopleIds.set(person.email, existing);
    }
  }

  // Map all Notion user IDs to team members, including duplicates by email
  for (const member of teamMembers) {
    const allNotionUserIds = new Set<string>(member.notionUserIds);

    // For each person in this team member, find all other people with same email
    for (const notionUserId of member.notionUserIds) {
      const person = people.find((p) => p.id === notionUserId);
      if (person?.email) {
        const duplicateIds = emailToPeopleIds.get(person.email);
        if (duplicateIds) {
          duplicateIds.forEach((id) => allNotionUserIds.add(id));
        }
      }
    }

    // Map all discovered IDs to this team member
    for (const notionUserId of allNotionUserIds) {
      output.set(notionUserId, member.id);
    }
  }

  return output;
};

/**
 * Build the inScope mapping that includes:
 * 1. Scope-based relationships (existing logic)
 * 2. Direct project ownership (via project.ownerIds)
 * 3. Direct task assignments (via task.assigneeIds)
 * 4. Filters out excluded projects and their related tasks
 *
 * Note: Scopes are filtered by selectedScopeStatus (Current/Future) and excludes "Archived" and "Complete" statuses
 */
const buildInScope = (
  scopes: Scope[],
  projects: Project[],
  tasks: Task[],
  selectedScopeStatus: SelectableScopeStatus,
  notionUIDToTeamMemberId: Map<string, string>,
  excludedProjects: Set<string>,
) => {
  const inScope: InScope = {
    projects: new Set(),
    scopes: new Set(),
    tasks: new Set(),
    memberEntitiesMap: new Map(),
  };

  const addEntity = (type: EntityType, entityId: string, assigneeIds: string[]) => {
    // Skip excluded projects
    if (type === "projects" && excludedProjects.has(entityId)) {
      return;
    }

    inScope[type].add(entityId);
    assigneeIds.forEach((id) => {
      const teamMemberId = notionUIDToTeamMemberId.get(id);
      if (teamMemberId) {
        if (inScope.memberEntitiesMap.has(teamMemberId)) {
          inScope.memberEntitiesMap.get(teamMemberId)?.push([type, entityId]);
        } else {
          inScope.memberEntitiesMap.set(teamMemberId, [[type, entityId]]);
        }
      }
    });
  };

  // 1. Add scope-based relationships (existing logic)
  // Filter scopes: include only the selected status (Current/Future), exclude "Archived" and "Complete"
  const alwaysExcludedStatuses = new Set(["Archived", "Complete"]);
  for (const scope of scopes) {
    // Include scopes matching the selected status, or non-standard statuses like "Background"
    // Always exclude "Archived" and "Complete"
    const shouldInclude =
      scope.status &&
      !alwaysExcludedStatuses.has(scope.status) &&
      (scope.status === selectedScopeStatus ||
        (scope.status !== "Current" && scope.status !== "Future"));

    if (shouldInclude) {
      for (const projectId of scope.comfyProjectIds) {
        if (!excludedProjects.has(projectId)) {
          addEntity("projects", projectId, scope.assigneeIds);
        }
      }
      for (const taskId of scope.comfyTaskIds) {
        // Check if task is associated with an excluded project
        const task = tasks.find((t) => t.id === taskId);
        if (task) {
          // Only add task if it's not associated with any excluded projects
          const hasExcludedProject = task.projectIds.some((pid) => excludedProjects.has(pid));
          if (!hasExcludedProject) {
            addEntity("tasks", taskId, scope.assigneeIds);
          }
        }
      }
      addEntity("scopes", scope.id, scope.assigneeIds);
    }
  }

  // 2. Add direct project ownership relationships
  for (const project of projects) {
    if (project.ownerIds.length > 0 && !excludedProjects.has(project.id)) {
      addEntity("projects", project.id, project.ownerIds);
    }
  }

  // 3. Add direct task assignment relationships
  for (const task of tasks) {
    // Skip tasks that belong to excluded projects
    const hasExcludedProject = task.projectIds.some((pid) => excludedProjects.has(pid));
    if (hasExcludedProject) {
      continue;
    }
    if (task.assigneeIds.length > 0) {
      addEntity("tasks", task.id, task.assigneeIds);
    }
  }

  return inScope;
};

const nodes: NodeDef<DagStore>[] = [
  {
    key: "teamMembers",
    deps: [
      (state) => state.people,
      (state) => state.teamTaskViews,
      (state) => state.memberExternalAccounts,
    ],
    compute: (state) => {
      return buildTeamMembers(state.people, state.teamTaskViews, state.memberExternalAccounts);
    },
  },
  {
    key: "teamMembersFiltered",
    deps: [(state) => state.teamMembers],
    compute: (state) => {
      return buildTeamMembersFiltered(state.teamMembers);
    },
  },
  {
    key: "teamMemberIndex",
    deps: [(state) => state.teamMembers],
    compute: (state) => {
      return buildTeamMemberIndex(state.teamMembers);
    },
  },
  {
    key: "allTeams",
    deps: [(state) => state.teamTaskViews, (state) => state.teamOrder],
    compute: (state) => {
      return buildAllTeams(state.teamTaskViews, state.teamOrder);
    },
  },
  {
    key: "allProjects",
    deps: [(state) => state.projects],
    compute: (state) => {
      return buildAllProjects(state.projects);
    },
  },
  {
    key: "projectIndex",
    deps: [(state) => state.projects],
    compute: (state) => {
      return buildProjectIndex(state.projects);
    },
  },
  {
    key: "taskIndex",
    deps: [(state) => state.tasks],
    compute: (state) => {
      return buildTaskIndex(state.tasks);
    },
  },
  {
    key: "scopeIndex",
    deps: [(state) => state.scopes],
    compute: (state) => {
      return buildScopeIndex(state.scopes);
    },
  },
  {
    key: "notionUIDToTeamMemberId",
    deps: [(state) => state.teamMembers, (state) => state.people],
    compute: (state) => {
      return buildNotionUIDToTeamMemberId(state.teamMembers, state.people);
    },
  },
  {
    key: "inScope",
    deps: [
      (state) => state.scopes,
      (state) => state.projects,
      (state) => state.tasks,
      (state) => state.selectedScopeStatus,
      (state) => state.notionUIDToTeamMemberId,
      (state) => state.excludedProjects,
    ],
    compute: (state) => {
      return buildInScope(
        state.scopes,
        state.projects,
        state.tasks,
        state.selectedScopeStatus,
        state.notionUIDToTeamMemberId,
        state.excludedProjects,
      );
    },
  },
];

export const useDagStore = create<DagStore>()(
  subscribeWithSelector((set, get) => ({
    // Initialize empty collections
    people: [],
    projects: [],
    tasks: [],
    scopes: [],
    teamTaskViews: [],
    recentOwnedItems: [],
    memberExternalAccounts: [],

    teamMembers: [],
    teamMembersFiltered: [],
    allTeams: [],
    allProjects: [],

    excludedTeams: new Set(),
    excludedProjects: new Set(),
    selectedScopeStatus: "Current",
    selectedRoleFilter: "Core Team",
    teamOrder: [],
    slackFilters: {
      startDate: startOfDay(new Date()),
      endDate: endOfDay(new Date()),
      teamMemberType: "all",
      roleFilter: "All",
    },
    cardsCollapsed: false,

    projectIndex: new Map(),
    taskIndex: new Map(),
    scopeIndex: new Map(),
    teamMemberIndex: new Map(),
    notionUIDToTeamMemberId: new Map(),

    inScope: {
      projects: new Set(),
      scopes: new Set(),
      tasks: new Set(),
      memberEntitiesMap: new Map(),
    },

    setRaw: (patch) => set(patch),
    setFilters: (patch) => set(patch),

    toggleTeam: (teamId: string): void => {
      const { excludedTeams: selectedTeams } = get();
      const next = new Set(selectedTeams);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      set({ excludedTeams: next });
    },
    toggleProject: (projectId: string): void => {
      const { excludedProjects: selectedProjects } = get();
      const next = new Set(selectedProjects);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      set({ excludedProjects: next });
    },
    toggleScopeStatus: (): void => {
      const { selectedScopeStatus } = get();
      set({ selectedScopeStatus: selectedScopeStatus === "Current" ? "Future" : "Current" });
    },
    setRoleFilter: (filter: RoleFilter): void => {
      set({ selectedRoleFilter: filter });
    },
    setExcludedTeams: (teams: string[]): void => {
      set({ excludedTeams: new Set(teams) });
    },
    setExcludedProjects: (projects: string[]): void => {
      set({ excludedProjects: new Set(projects) });
    },
    resetFilters: (): void => {
      set({
        excludedTeams: new Set(),
        excludedProjects: new Set(),
        selectedScopeStatus: "Current",
        selectedRoleFilter: "Core Team",
      });
    },
    setTeamOrder: (order: string[]): void => {
      set({ teamOrder: order });
      // Save to localStorage
      localStorage.setItem(
        "team-dash-team-order",
        JSON.stringify({
          version: 1,
          teamOrder: order,
          lastUpdated: new Date().toISOString(),
        }),
      );
    },
    initializeRandomTeamOrder: (): void => {
      const { allTeams } = get();
      if (allTeams.length === 0) return;

      // Check if order already exists in localStorage
      try {
        const saved = localStorage.getItem("team-dash-team-order");
        if (saved) {
          const parsed = JSON.parse(saved) as { version?: number; teamOrder?: unknown };
          if (parsed.version === 1 && Array.isArray(parsed.teamOrder)) {
            set({ teamOrder: parsed.teamOrder as string[] });
            return;
          }
        }
      } catch (error) {
        console.warn("Failed to load saved team order:", error);
      }

      // Create randomized order
      const shuffled = [...allTeams].sort(() => Math.random() - 0.5);
      get().setTeamOrder(shuffled);
    },
    setSlackFilters: (filters: SlackFilters): void => {
      set({ slackFilters: filters });
    },

    toggleCardsCollapsed: (): void => {
      set((state) => ({ cardsCollapsed: !state.cardsCollapsed }));
    },
  })),
);

/**
 * Sets up all the dependencies between the nodes in the store.
 */
wireNodes(useDagStore, nodes);
